'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getProductCategoriesByCompany,
    deleteProductCategory,
    ProductCategoryItem,
} from '@/lib/api/product-category/product-category-api'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdClose,
    MdCategory,
    MdSubdirectoryArrowRight,
    MdExpandMore,
    MdExpandLess,
    MdFileDownload,
} from 'react-icons/md'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import { ShareableModule } from '@/lib/api/share/share-api'
import { useNavigate } from 'react-router-dom'
import ConfirmModal from '@/components/confirm-modal'

export default function ProductCategoryListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'commerce',
        'products'
    )
    const [allItems, setAllItems] = useState<ProductCategoryItem[]>([])
    const [filteredItems, setFilteredItems] = useState<ProductCategoryItem[]>(
        []
    )
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [categoryToDelete, setCategoryToDelete] = useState<number | null>(
        null
    )
    const [deleting, setDeleting] = useState(false)
    const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
        new Set()
    )
    const [showImportModal, setShowImportModal] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadCategories()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase()
            const filtered = allItems.filter(
                item =>
                    item.name.toLowerCase().includes(term) ||
                    item.parent?.name?.toLowerCase().includes(term)
            )
            setFilteredItems(filtered)
        } else {
            setFilteredItems(allItems)
        }
    }, [searchTerm, allItems])

    const loadCategories = async () => {
        if (!user?.companyId) return

        setLoading(true)
        try {
            const response = await getProductCategoriesByCompany(user.companyId)
            if (response.success) {
                setAllItems(response.result)
                setFilteredItems(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar categorias')
            }
        } catch (error) {
            toast.error('Erro ao carregar categorias')
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
    }

    const handleDelete = (id: number) => {
        setCategoryToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!categoryToDelete) return

        setDeleting(true)
        try {
            const response = await deleteProductCategory(categoryToDelete)
            if (response.success) {
                toast.success('Categoria excluída com sucesso')
                loadCategories()
                setShowDeleteModal(false)
                setCategoryToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir categoria')
            }
        } catch (error) {
            toast.error('Erro ao excluir categoria')
        } finally {
            setDeleting(false)
        }
    }

    const toggleExpand = (id: number) => {
        setExpandedCategories(prev => {
            const next = new Set(prev)
            if (next.has(id)) {
                next.delete(id)
            } else {
                next.add(id)
            }
            return next
        })
    }

    const categories = filteredItems.filter(item => !item.isSubcategory)
    const subcategories = filteredItems.filter(item => item.isSubcategory)
    const totalCategories = allItems.filter(item => !item.isSubcategory).length
    const totalSubcategories = allItems.filter(
        item => item.isSubcategory
    ).length

    const getSubcategoriesForParent = (parentId: number) => {
        return subcategories.filter(sub => sub.parentId === parentId)
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando categorias de produtos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdCategory className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Categorias de Produtos
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {totalCategories}{' '}
                            {totalCategories === 1 ? 'categoria' : 'categorias'}
                            {totalSubcategories > 0 && (
                                <>
                                    {' '}
                                    e {totalSubcategories}{' '}
                                    {totalSubcategories === 1
                                        ? 'subcategoria'
                                        : 'subcategorias'}
                                </>
                            )}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <div className="flex items-center gap-2">
                        <button
                            className="btn btn-outline btn-secondary gap-2"
                            onClick={() => setShowImportModal(true)}
                        >
                            <MdFileDownload className="text-xl" />
                            <span className="hidden sm:inline">Importar</span>
                        </button>
                        <button
                            className="btn btn-outline btn-secondary gap-2"
                            onClick={() =>
                                navigate(
                                    '/registrations/product-categories/new?tipo=subcategoria'
                                )
                            }
                        >
                            <MdSubdirectoryArrowRight className="text-xl" />
                            <span className="hidden sm:inline">
                                Subcategoria
                            </span>
                        </button>
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() =>
                                navigate(
                                    '/registrations/product-categories/new'
                                )
                            }
                        >
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Categoria</span>
                        </button>
                    </div>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <form
                    onSubmit={handleSearch}
                    className="flex items-center gap-2"
                >
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </form>
            </div>

            {filteredItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdCategory className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhuma categoria encontrada'
                                : 'Nenhuma categoria cadastrada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando categorias de produtos'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() =>
                                    navigate(
                                        '/registrations/product-categories/new'
                                    )
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeira categoria
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {categories.map(category => {
                        const subs = getSubcategoriesForParent(category.id)
                        const isExpanded = expandedCategories.has(category.id)
                        const hasSubcategories = subs.length > 0

                        return (
                            <div
                                key={category.id}
                                className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden"
                            >
                                <div className="flex items-center justify-between p-4">
                                    <div className="flex items-center gap-3">
                                        {hasSubcategories ? (
                                            <button
                                                onClick={() =>
                                                    toggleExpand(category.id)
                                                }
                                                className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center hover:bg-secondary/20 transition-colors"
                                            >
                                                {isExpanded ? (
                                                    <MdExpandLess className="text-xl text-secondary" />
                                                ) : (
                                                    <MdExpandMore className="text-xl text-secondary" />
                                                )}
                                            </button>
                                        ) : (
                                            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                                <MdCategory className="text-xl text-secondary" />
                                            </div>
                                        )}
                                        <div>
                                            <h3 className="text-sm sm:text-base font-semibold text-base-content">
                                                {category.name}
                                            </h3>
                                            <p className="text-xs text-base-content/50">
                                                {hasSubcategories && (
                                                    <span className="mr-2">
                                                        {subs.length}{' '}
                                                        {subs.length === 1
                                                            ? 'subcategoria'
                                                            : 'subcategorias'}
                                                    </span>
                                                )}
                                                Criada em{' '}
                                                {new Date(
                                                    category.createdAt
                                                ).toLocaleDateString('pt-BR')}
                                            </p>
                                        </div>
                                    </div>

                                    {(canUpdate || canDelete) && (
                                        <div className="flex items-center gap-2">
                                            {canUpdate && (
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/registrations/product-categories/${category.id}`
                                                        )
                                                    }
                                                    className="btn btn-xs sm:btn-sm btn-ghost"
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-base sm:text-lg" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={() =>
                                                        handleDelete(
                                                            category.id
                                                        )
                                                    }
                                                    className="btn btn-xs sm:btn-sm btn-ghost text-error hover:bg-error/10"
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-base sm:text-lg" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {hasSubcategories && isExpanded && (
                                    <div className="border-t border-base-300/50 bg-base-200/20">
                                        {subs.map(sub => (
                                            <div
                                                key={sub.id}
                                                className="flex items-center justify-between px-4 py-3 border-b border-base-300/30 last:border-b-0"
                                            >
                                                <div className="flex items-center gap-3 pl-4">
                                                    <MdSubdirectoryArrowRight className="text-base-content/40" />
                                                    <div>
                                                        <h4 className="text-sm font-medium text-base-content">
                                                            {sub.name}
                                                        </h4>
                                                        <p className="text-xs text-base-content/50">
                                                            Criada em{' '}
                                                            {new Date(
                                                                sub.createdAt
                                                            ).toLocaleDateString(
                                                                'pt-BR'
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>

                                                {(canUpdate || canDelete) && (
                                                    <div className="flex items-center gap-2">
                                                        {canUpdate && (
                                                            <button
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/registrations/product-categories/${sub.id}`
                                                                    )
                                                                }
                                                                className="btn btn-xs btn-ghost"
                                                                title="Editar"
                                                            >
                                                                <MdEdit className="text-base" />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                onClick={() =>
                                                                    handleDelete(
                                                                        sub.id
                                                                    )
                                                                }
                                                                className="btn btn-xs btn-ghost text-error hover:bg-error/10"
                                                                title="Excluir"
                                                            >
                                                                <MdDelete className="text-base" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}

                    {/* Subcategorias órfãs (sem categoria pai visível na busca) */}
                    {searchTerm &&
                        subcategories
                            .filter(
                                sub =>
                                    !categories.some(
                                        cat => cat.id === sub.parentId
                                    )
                            )
                            .map(sub => (
                                <div
                                    key={sub.id}
                                    className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden"
                                >
                                    <div className="flex items-center justify-between p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                                <MdSubdirectoryArrowRight className="text-xl text-secondary" />
                                            </div>
                                            <div>
                                                <h3 className="text-sm sm:text-base font-semibold text-base-content">
                                                    {sub.name}
                                                </h3>
                                                <p className="text-xs text-base-content/50">
                                                    Subcategoria de{' '}
                                                    <span className="font-medium">
                                                        {sub.parent?.name ??
                                                            'N/A'}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>

                                        {(canUpdate || canDelete) && (
                                            <div className="flex items-center gap-2">
                                                {canUpdate && (
                                                    <button
                                                        onClick={() =>
                                                            navigate(
                                                                `/registrations/product-categories/${sub.id}`
                                                            )
                                                        }
                                                        className="btn btn-xs sm:btn-sm btn-ghost"
                                                        title="Editar"
                                                    >
                                                        <MdEdit className="text-base sm:text-lg" />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        onClick={() =>
                                                            handleDelete(sub.id)
                                                        }
                                                        className="btn btn-xs sm:btn-sm btn-ghost text-error hover:bg-error/10"
                                                        title="Excluir"
                                                    >
                                                        <MdDelete className="text-base sm:text-lg" />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                </div>
            )}

            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.PRODUCT_CATEGORY}
                moduleLabel="Categorias de Produtos"
                fetchItems={async (companyId: number) => {
                    const response =
                        await getProductCategoriesByCompany(companyId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: ProductCategoryItem) => (
                    <div>
                        <div className="flex items-center gap-2">
                            {item.isSubcategory && (
                                <MdSubdirectoryArrowRight className="text-base-content/40 flex-shrink-0" />
                            )}
                            <p className="text-sm font-medium truncate">
                                {item.name}
                            </p>
                        </div>
                        <p className="text-xs text-base-content/60 truncate">
                            {item.isSubcategory
                                ? `Subcategoria de ${item.parent?.name || 'N/A'}`
                                : 'Categoria'}
                        </p>
                    </div>
                )}
                getItemId={(item: ProductCategoryItem) => item.id}
                getItemName={(item: ProductCategoryItem) => item.name}
                onImportComplete={loadCategories}
            />

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setCategoryToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir categoria"
                message="Tem certeza de que deseja excluir esta categoria? Produtos vinculados a ela perderão a associação."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
